import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, Play, DollarSign, Users, TrendingUp, Crown, Gift,
  Sparkles, Receipt, Percent, Trophy, ArrowRight, CheckCircle2, Circle, Rocket, X, Star, LineChart, Grid3x3, Download,
  Award, Flame, AlertTriangle, Bug, HeartPulse, Clock, Share2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatCard, EmptyState } from "@/components/Bits";
import OnboardingCard from "@/components/OnboardingCard";
import ShareStorefrontCard from "@/components/ShareStorefrontCard";
import GameAnalyticsSheet from "@/components/GameAnalyticsSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cineCounts, setCineCounts] = useState({});
  const [analyticsId, setAnalyticsId] = useState(null);
  const [cohortInsight, setCohortInsight] = useState(null);
  const [insightHistory, setInsightHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const openInsightHistory = () => {
    setHistoryOpen(true);
    api.get("/creator/cohort-insight/history").then((r) => setInsightHistory(r.data.history || [])).catch(() => setInsightHistory([]));
  };

  useEffect(() => {
    api.get("/creator/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/cinematic/interest/mine")
      .then((r) => setCineCounts(r.data.counts || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (data?.cohorts?.rows?.length) {
      api.get("/creator/cohort-insight").then((r) => setCohortInsight(r.data.insight)).catch(() => {});
    }
  }, [data?.cohorts]);

  if (!data) return <Spinner />;
  const t = data.totals;

  if (t.games === 0) {
    return (
      <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="creator-dashboard">
        <PageHeader title="Creator Dashboard" icon={BarChart3}
          subtitle="Track plays, conversions and revenue across all your games." />
        <OnboardingCard navigate={navigate} />
        <EmptyState emoji="🎮" title="No games yet"
          text="Create and publish a game to start seeing analytics here."
          action={<Button onClick={() => navigate("/app/create")} className="rounded-full" data-testid="create-first-game">Create a game</Button>} />
      </div>
    );
  }

  const freePct = t.total_plays ? Math.round((t.free_plays / t.total_plays) * 100) : 0;
  const premiumPct = t.total_plays ? 100 - freePct : 0;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="creator-dashboard">
      <PageHeader title="Creator Dashboard" icon={BarChart3}
        subtitle="Track plays, conversions and revenue across all your games."
        action={<Button onClick={() => navigate("/app/earnings")} variant="outline" className="rounded-full" data-testid="go-earnings">
          <DollarSign className="w-4 h-4" /> Earnings & payouts</Button>} />

      <OnboardingCard navigate={navigate} />

      <ShareStorefrontCard userId={user?.id} />

      <RatingAlerts alerts={data.rating_alerts} navigate={navigate} />
      <TrophyShelf badges={data.qa_badges} navigate={navigate} />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total plays" value={t.total_plays.toLocaleString()} icon={Play} accent="#3b9eff" />
        <div data-testid="creator-avg-rating"><StatCard label="Avg rating" value={t.avg_rating ? `${t.avg_rating}★` : "—"} icon={Star} accent="#eab308" delay={1} /></div>
        <StatCard label="Est. revenue" value={usd(t.revenue)} icon={DollarSign} accent="#22c55e" delay={2} />
        <StatCard label="Pack buyers" value={t.total_buyers} icon={Crown} accent="#f59e0b" delay={3} />
        <StatCard label="Conversion" value={`${t.conversion}%`} icon={Percent} accent="#a855f7" delay={4} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Free vs premium plays */}
        <Section title="Free vs premium plays" icon={Gift}>
          <div className="flex items-end gap-2 mb-3">
            <span className="text-3xl font-display font-extrabold tabular-nums">{t.total_plays.toLocaleString()}</span>
            <span className="text-sm text-[hsl(var(--muted-foreground))] mb-1">total plays</span>
          </div>
          <div className="h-4 rounded-full overflow-hidden bg-[hsl(var(--secondary))] flex" data-testid="free-premium-bar">
            <div className="h-full bg-[#3b9eff]" style={{ width: `${freePct}%` }} title={`Free ${freePct}%`} />
            <div className="h-full bg-[#f59e0b]" style={{ width: `${premiumPct}%` }} title={`Premium ${premiumPct}%`} />
          </div>
          <div className="flex justify-between text-xs mt-2 text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-[#3b9eff] inline-block" /> Free games · {t.free_plays.toLocaleString()} ({freePct}%)</span>
            <span className="flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-[#f59e0b] inline-block" /> Premium · {t.premium_plays.toLocaleString()} ({premiumPct}%)</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5 text-center">
            <Mini label="Games" value={t.games} />
            <Mini label="With packs" value={t.premium_games} />
            <Mini label="ARPU" value={usd(t.arpu)} />
          </div>
        </Section>

        {/* Drop-off / retention funnel */}
        <Section title="Player retention" icon={TrendingUp}>
          {!data.funnel ? (
            <Empty text="Publish a multi-level campaign to see how far players progress." />
          ) : (
            <div data-testid="retention-funnel">
              <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                {data.funnel.title} · {data.funnel.starters} player{data.funnel.starters === 1 ? "" : "s"} started
              </div>
              <div className="space-y-2">
                {data.funnel.steps.map((s) => (
                  <div key={s.level} className="flex items-center gap-2 text-xs">
                    <span className="w-16 shrink-0 text-[hsl(var(--muted-foreground))]">Lv {s.level}</span>
                    <div className="flex-1 h-3 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" style={{ width: `${s.pct}%` }} />
                    </div>
                    <span className="w-20 shrink-0 text-right tabular-nums">{s.players} · {s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Returning-players — weekly cohort grid (falls back to trend line) */}
      {data.cohorts?.rows?.length > 0 ? (
        <Section title="Returning players — weekly cohorts" icon={Grid3x3} className="mb-6">
          {cohortInsight && (
            <div className="flex items-center gap-2 mb-3 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2" data-testid="cohort-insight">
              <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] shrink-0" />
              <p className="text-xs leading-snug flex-1">{cohortInsight}</p>
              <button onClick={openInsightHistory} title="Insight history" className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="cohort-insight-history-btn">
                <Clock className="w-3.5 h-3.5" />
              </button>
              <span className="text-[9px] font-bold uppercase tracking-wide text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/40 rounded-full px-1.5 py-0.5 shrink-0">AI</span>
            </div>
          )}
          <CohortGrid cohorts={data.cohorts} />
        </Section>
      ) : data.retention?.weeks?.length > 0 ? (
        <Section title="Returning players trend" icon={LineChart} className="mb-6">
          <RetentionTrend weeks={data.retention.weeks} />
        </Section>
      ) : null}

      {/* Per-game performance table */}
      <Section title="Game performance" icon={BarChart3} className="mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="game-performance-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="py-2 pr-3 font-semibold">Game</th>
                <th className="py-2 px-3 font-semibold text-right">Plays</th>
                <th className="py-2 px-3 font-semibold text-right">Rating</th>
                <th className="py-2 px-3 font-semibold text-right">Buyers</th>
                <th className="py-2 px-3 font-semibold text-right">Conv.</th>
                <th className="py-2 px-3 font-semibold text-right">Revenue</th>
                <th className="py-2 px-3 font-semibold text-right">Levels</th>
                <th className="py-2 pl-3 font-semibold text-right">Analytics</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {data.games.map((g) => (
                <tr key={g.id} className="hover:bg-[hsl(var(--secondary))]/40 cursor-pointer"
                  onClick={() => navigate(g.has_levels ? `/app/levels/${g.id}` : `/app/build/${g.id}`)}
                  data-testid={`perf-row-${g.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-lg shrink-0">{g.cover_emoji}</span>
                      <div className="min-w-0">
                        <div className="font-semibold truncate flex items-center gap-1.5">
                          {g.title}
                          {g.premium && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
                        </div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] capitalize">{g.status}</div>
                        {cineCounts[g.id] > 0 && (
                          <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-500" data-testid={`cine-demand-${g.id}`}>
                            🎬 {cineCounts[g.id]} want Cinematic Mode
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{g.plays.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{g.rating_count ? <span className="text-amber-500 font-semibold">{g.rating}★</span> : "—"}<span className="text-[10px] text-[hsl(var(--muted-foreground))]">{g.rating_count ? ` (${g.rating_count})` : ""}</span></td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{g.premium ? g.buyers : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{g.premium ? `${g.conversion}%` : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-emerald-500">{g.revenue > 0 ? usd(g.revenue) : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums">{g.has_levels ? g.level_count : "—"}</td>
                  <td className="py-2.5 pl-3 text-right">
                    <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs"
                      onClick={(e) => { e.stopPropagation(); setAnalyticsId(g.id); }}
                      data-testid={`perf-analytics-${g.id}`}>
                      <LineChart className="w-3.5 h-3.5" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Top earners" icon={Trophy}>
          {data.top_earning.length === 0 ? (
            <Empty text="No paid sales yet. Enable a premium level pack in Level Studio to start earning." />
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="top-earners">
              {data.top_earning.map((g, i) => (
                <div key={g.id} className="flex items-center gap-3 py-2.5">
                  <span className="text-xs font-bold w-5 text-[hsl(var(--muted-foreground))]">#{i + 1}</span>
                  <span className="text-lg">{g.cover_emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{g.title}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{g.buyers} buyer{g.buyers === 1 ? "" : "s"} · {usd(g.price)} pack</div>
                  </div>
                  <div className="font-bold tabular-nums text-emerald-500">{usd(g.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Recent sales" icon={Receipt}>
          {data.recent_sales.length === 0 ? (
            <Empty text="Your level-pack sales will appear here." />
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="recent-sales">
              {data.recent_sales.map((s, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {s.title}
                      {s.shared && <Badge variant="outline" className="text-[9px]">split</Badge>}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{s.buyer_name} · {fmtDate(s.created_at)}</div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums text-emerald-500">{usd(s.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="Events & competitions" icon={Trophy} className="mt-6">
        {(!data.events || data.events.length === 0) ? (
          <Empty text="You haven't joined any events yet. Enter a seasonal event to compete on the leaderboard." />
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]" data-testid="creator-events">
            {data.events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-3 py-2.5" data-testid={`creator-event-${ev.id}`}>
                <span className="text-lg">{ev.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{ev.title}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] capitalize">{ev.status || "event"}{ev.ends_at ? ` · ends ${fmtDate(ev.ends_at)}` : ""}</div>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]"><Users className="w-3 h-3 inline mr-0.5" />{ev.participants} in</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[#1f6feb]/10 to-transparent p-5 mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <Sparkles className="w-6 h-6 text-[hsl(var(--primary))] shrink-0" />
        <div className="flex-1">
          <div className="font-bold">Grow your revenue</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]">Use the AI Growth Coach in Level Studio for pricing, retention and conversion tips tailored to your games.</div>
        </div>
        {data.top_games[0] && (
          <Button onClick={() => navigate(`/app/levels/${data.top_games[0].id}`)} className="rounded-full shrink-0" data-testid="open-growth-coach">
            Open Level Studio <ArrowRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      <GameAnalyticsSheet gameId={analyticsId} open={!!analyticsId} onClose={() => setAnalyticsId(null)} />

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md" data-testid="cohort-insight-history">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-[hsl(var(--primary))]" /> Your retention story</DialogTitle>
          </DialogHeader>
          {insightHistory === null ? (
            <div className="py-8 grid place-items-center"><Spinner /></div>
          ) : insightHistory.length === 0 ? (
            <p className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))]">No insights logged yet — check back as your cohorts grow.</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {insightHistory.map((h, i) => (
                <div key={i} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2" data-testid={`insight-history-${i}`}>
                  <p className="text-xs leading-snug">{h.insight}</p>
                  <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{fmtDate(h.ts)}</p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const CohortGrid = ({ cohorts }) => {
  const { labels, rows } = cohorts;
  const [drill, setDrill] = useState(null);   // {row, col} labels
  const [detail, setDetail] = useState(null);  // null=loading, {} = result

  const openCell = async (row, cell) => {
    setDrill({ rowLabel: row.label, colLabel: labels[cell.offset], pct: cell.pct });
    setDetail(null);
    try {
      const { data } = await api.get(`/creator/cohort-detail?cohort=${row.week}&offset=${cell.offset}`);
      setDetail(data);
    } catch (e) { setDetail({ error: true }); }
  };

  return (
    <div data-testid="cohort-grid">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Each row is the week a group of players first joined. Click a cell to see which games brought them back — deeper teal means stickier.
        </p>
        <Button variant="outline" size="sm" className="rounded-full shrink-0 text-xs h-7" onClick={() => exportCohortCsv(cohorts)} data-testid="cohort-export-csv">
          <Download className="w-3.5 h-3.5" /> CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-separate" style={{ borderSpacing: "4px" }}>
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              <th className="text-left font-semibold pr-2">Joined</th>
              <th className="text-right font-semibold pr-2">Players</th>
              {labels.map((l) => <th key={l} className="font-semibold text-center min-w-[44px]">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} data-testid={`cohort-row-${ri}`}>
                <td className="text-xs font-medium whitespace-nowrap pr-2">{row.label}</td>
                <td className="text-xs tabular-nums text-right pr-2 text-[hsl(var(--muted-foreground))]">{row.size}</td>
                {labels.map((_, k) => {
                  const cell = row.cells.find((c) => c.offset === k);
                  if (!cell) return <td key={k} />;
                  return (
                    <td key={k} className="text-center">
                      <button
                        className="w-full rounded-md py-1.5 text-[11px] font-bold tabular-nums transition-transform hover:scale-[1.08] focus:outline-none focus:ring-2 focus:ring-teal-400/60"
                        style={{ background: cohortColor(cell.pct), color: cell.pct >= 45 ? "#fff" : "hsl(var(--foreground))" }}
                        title={`${cell.count}/${row.size} active — click for game breakdown`}
                        onClick={() => openCell(row, cell)}
                        data-testid={`cohort-cell-${ri}-${k}`}>
                        {cell.pct}%
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!drill} onOpenChange={(v) => { if (!v) { setDrill(null); setDetail(null); } }}>
        <DialogContent className="max-w-md" data-testid="cohort-drill-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {drill?.rowLabel} cohort · {drill?.colLabel}
            </DialogTitle>
          </DialogHeader>
          {!detail ? (
            <div className="py-8 grid place-items-center"><Spinner /></div>
          ) : detail.error ? (
            <p className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))]">Couldn't load the breakdown.</p>
          ) : detail.games.length === 0 ? (
            <p className="py-6 text-center text-sm text-[hsl(var(--muted-foreground))]">No returning players from this cohort played that week.</p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  <b className="text-[hsl(var(--foreground))]">{detail.players}</b> of {detail.cohort_size} players from this cohort came back — via these games:
                </p>
                <Button variant="outline" size="sm" className="rounded-full shrink-0 text-xs h-7"
                  onClick={() => exportDrillCsv(drill, detail)} data-testid="cohort-drill-export-csv">
                  <Download className="w-3.5 h-3.5" /> CSV
                </Button>
              </div>
              {detail.games.map((g) => (
                <div key={g.game_id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2" data-testid={`cohort-drill-game-${g.game_id}`}>
                  <span className="text-xl">{g.cover_emoji}</span>
                  <span className="text-sm font-medium flex-1 truncate">{g.title}</span>
                  <span className="text-sm font-bold tabular-nums text-teal-500">{g.players}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BADGE_TIERS = {
  "QA Legend": { Icon: Trophy, cls: "from-fuchsia-500/20 to-amber-500/10 border-fuchsia-500/50", color: "#e879f9" },
  "QA Pro": { Icon: Award, cls: "from-amber-500/20 to-orange-500/10 border-amber-500/50", color: "#f59e0b" },
  "On a roll": { Icon: Flame, cls: "from-orange-500/20 to-rose-500/10 border-orange-500/40", color: "#fb923c" },
  "Big glow-up": { Icon: Sparkles, cls: "from-emerald-500/20 to-teal-500/10 border-emerald-500/40", color: "#34d399" },
  "Recovered!": { Icon: HeartPulse, cls: "from-emerald-500/25 to-green-500/10 border-emerald-500/60", color: "#10b981" },
};

const shareBadge = async (b) => {
  const caption = b.kind === "recovery"
    ? `🎉 I brought "${b.game_title}" back from a ${b.recovered_from}★ dip to ${b.recovered_to}★ on GameView Studio! 🎮`
    : b.kind === "gain"
      ? `📈 "${b.game_title}" jumped +${b.gain} in AI playtest score on GameView Studio! 🎮`
      : `🏆 "${b.tier}" unlocked on "${b.game_title}" — ${b.fixes} AI playtest fixes on GameView Studio! 🎮`;
  try {
    if (navigator.share) await navigator.share({ title: "GameView Studio", text: caption });
    else { await navigator.clipboard.writeText(caption); toast.success("Badge copied — paste it anywhere! 🎉"); }
  } catch (e) { /* dismissed */ }
};

const TrophyShelf = ({ badges, navigate }) => {
  if (!badges || badges.length === 0) return null;
  return (
    <Section title="QA trophies" icon={Trophy} className="mb-6">
      <div className="flex flex-wrap gap-3" data-testid="trophy-shelf">
        {badges.map((b, i) => {
          const t = BADGE_TIERS[b.tier] || BADGE_TIERS["On a roll"];
          const Icon = t.Icon;
          const isProgress = b.status === "progress";
          const idBase = isProgress ? "trophy-progress" : `trophy-${b.tier.replace(/\s+/g, "-").toLowerCase()}`;
          return (
            <div key={i}
              className={`flex items-center gap-2 rounded-2xl border pr-2 transition-transform hover:scale-[1.02] ${isProgress ? "border-dashed border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30" : `bg-gradient-to-br ${t.cls}`}`}
              data-testid={`${idBase}-${b.game_id}`}>
              <button onClick={() => navigate(`/app/qa/${b.game_id}`)} className="flex items-center gap-3 px-4 py-3 text-left min-w-0">
                <div className="grid place-items-center w-10 h-10 rounded-full bg-[hsl(var(--background))]/40 shrink-0">
                  <Icon className={`w-5 h-5 ${isProgress ? "opacity-40" : ""}`} style={{ color: isProgress ? "hsl(var(--muted-foreground))" : t.color }} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold leading-tight flex items-center gap-1.5">
                    {isProgress ? <span className="text-[hsl(var(--muted-foreground))]">In progress</span> : b.tier}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate flex items-center gap-1">
                    <span>{b.cover_emoji}</span>{b.game_title}
                    <span className="opacity-70">· {b.kind === "gain" ? `+${b.gain}` : b.kind === "recovery" ? `${b.recovered_from}★→${b.recovered_to}★` : `${b.fixes} fixes`}</span>
                  </div>
                  {b.to_next != null && b.next_tier && (
                    <div className="text-[10px] font-semibold text-[hsl(var(--primary))] mt-0.5" data-testid={`trophy-next-${b.game_id}`}>
                      {b.to_next} {b.to_next === 1 ? "fix" : "fixes"} to {b.next_tier}
                    </div>
                  )}
                </div>
              </button>
              {!isProgress && (
                <button onClick={() => shareBadge(b)} title="Share this win"
                  className="shrink-0 w-8 h-8 grid place-items-center rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/10 transition-colors"
                  data-testid={`trophy-share-${b.game_id}`}>
                  <Share2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const RatingAlerts = ({ alerts, navigate }) => {
  const [dismissed, setDismissed] = useState([]);
  const shown = (alerts || []).filter((a) => !dismissed.includes(a.game_id));
  const snooze = async (gid) => {
    try {
      await api.post("/creator/rating-alert/snooze", { game_id: gid });
      setDismissed((d) => [...d, gid]);
      toast.success("Snoozed for a week ⏰", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.delete(`/creator/rating-alert/snooze/${gid}`);
              setDismissed((d) => d.filter((x) => x !== gid));
              toast.success("Alert restored");
            } catch (e) { toast.error("Couldn't undo."); }
          },
        },
      });
    } catch (e) { toast.error("Couldn't snooze that alert."); }
  };
  if (shown.length === 0) return null;
  return (
    <div className="space-y-2 mb-6" data-testid="rating-alerts">
      {shown.map((a) => (
        <div key={a.game_id} className="flex items-center gap-3 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3" data-testid={`rating-alert-${a.game_id}`}>
          <div className="grid place-items-center w-9 h-9 rounded-full bg-rose-500/20 shrink-0">
            <AlertTriangle className="w-4.5 h-4.5 text-rose-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight flex items-center gap-1.5">
              <span>{a.cover_emoji}</span><span className="truncate">{a.game_title}</span>
              <span className="font-normal text-[hsl(var(--muted-foreground))]">rating is slipping</span>
            </p>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
              Dropped from <b className="text-[hsl(var(--foreground))]">{a.prev_avg}★</b> to <b className="text-rose-500">{a.recent_avg}★</b> over its last {a.recent_count} ratings.
            </p>
          </div>
          <Button size="sm" className="rounded-full shrink-0" onClick={() => navigate(`/app/qa/${a.game_id}`)} data-testid={`rating-alert-playtest-${a.game_id}`}>
            <Bug className="w-3.5 h-3.5" /> Run a playtest
          </Button>
          <Button size="sm" variant="ghost" className="rounded-full shrink-0 text-xs" onClick={() => snooze(a.game_id)} data-testid={`rating-alert-snooze-${a.game_id}`}>
            <Clock className="w-3.5 h-3.5" /> Snooze 1w
          </Button>
          <button onClick={() => setDismissed((d) => [...d, a.game_id])} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] shrink-0" data-testid={`rating-alert-dismiss-${a.game_id}`} aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

const cohortColor = (pct) => `rgba(20, 184, 166, ${Math.max(0.06, pct / 100)})`;

const exportDrillCsv = (drill, detail) => {
  const rows = [
    ["Cohort joined", drill.rowLabel],
    ["Active week", drill.colLabel],
    ["Returning players", detail.players],
    ["Cohort size", detail.cohort_size],
    [],
    ["Game", "Returning players"],
    ...detail.games.map((g) => [g.title, g.players]),
  ];
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cohort-${String(drill.rowLabel).replace(/\s+/g, "")}-${drill.colLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};


const exportCohortCsv = (cohorts) => {
  const { labels, rows } = cohorts;
  const header = ["Joined", "Players", ...labels];
  const body = rows.map((r) => {
    const byOffset = {};
    r.cells.forEach((c) => { byOffset[c.offset] = `${c.pct}%`; });
    return [r.label, r.size, ...labels.map((_, k) => (byOffset[k] ?? ""))];
  });
  const csv = [header, ...body].map((row) => row.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "cohort-retention.csv"; a.click();
  URL.revokeObjectURL(url);
};

const RetentionTrend = ({ weeks }) => {
  const n = weeks.length;
  const max = Math.max(1, ...weeks.map((w) => w.players));
  const W = 640, H = 120, pad = 12;
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - (v / max) * (H - 2 * pad);
  const total = weeks.map((w, i) => `${x(i)},${y(w.players)}`).join(" ");
  const ret = weeks.map((w, i) => `${x(i)},${y(w.returning)}`).join(" ");
  const avgPct = Math.round(weeks.reduce((a, w) => a + w.pct, 0) / n);
  return (
    <div data-testid="retention-trend">
      <div className="flex items-center gap-4 mb-3 text-xs">
        <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 rounded bg-[#a855f7] inline-block" /> Weekly players</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-0.5 rounded bg-[#14b8a6] inline-block" /> Returning</span>
        <span className="ml-auto text-[hsl(var(--muted-foreground))]">Avg return rate <b className="text-[hsl(var(--foreground))]" data-testid="retention-avg-pct">{avgPct}%</b></span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
        <polyline points={total} fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={ret} fill="none" stroke="#14b8a6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {weeks.map((w, i) => <circle key={`t${i}`} cx={x(i)} cy={y(w.players)} r="3" fill="#a855f7" />)}
        {weeks.map((w, i) => <circle key={`r${i}`} cx={x(i)} cy={y(w.returning)} r="3" fill="#14b8a6" />)}
      </svg>
      <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
        {weeks.map((w, i) => <span key={i}>{w.label}</span>)}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 ${className}`}>
    <h2 className="font-bold mb-3 flex items-center gap-2"><Icon className="w-4 h-4 text-[hsl(var(--primary))]" /> {title}</h2>
    {children}
  </div>
);
const Empty = ({ text }) => <div className="text-sm text-[hsl(var(--muted-foreground))] py-4">{text}</div>;
const Mini = ({ label, value }) => (
  <div className="rounded-xl bg-[hsl(var(--secondary))]/50 py-2.5">
    <div className="font-display text-xl font-extrabold tabular-nums">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">{label}</div>
  </div>
);
